import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, BarChart3, Euro, TrendingUp, TrendingDown, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { calculateBalances } from '../lib/services/depensesPartageesService';
import { getParticipants } from '../lib/services/participantsService';
import { DepensePartagee, Participant } from '../types/shared-expenses';

interface ParticipantSummary {
  participant: Participant;
  totalPaye: number;
  totalDu: number;
  solde: number;
}

interface Balance {
  from: string;
  to: string;
  montant: number;
  fromNom: string;
  toNom: string;
}

const SharedExpensesSummary: React.FC = () => {
  const navigate = useNavigate();
  const { annee, mois } = useParams<{ annee: string; mois: string }>();
  const [depenses, setDepenses] = useState<DepensePartagee[]>([]);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [participantsSummary, setParticipantsSummary] = useState<ParticipantSummary[]>([]);
  const [balances, setBalances] = useState<Balance[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalDepenses, setTotalDepenses] = useState(0);

  useEffect(() => {
    if (annee && mois) {
      loadData();
    }
  }, [annee, mois]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Charger les participants
      const participantsResult = await getParticipants();
      if (participantsResult.error) throw participantsResult.error;
      if (participantsResult.data) {
        setParticipants(participantsResult.data);
      }

      // Calculer les balances avec calculateBalances
      const balancesResult = await calculateBalances(mois!, annee!);
      if (balancesResult.error) throw balancesResult.error;

      if (balancesResult.data) {
        if (balancesResult.data.depenses) {
          setDepenses(balancesResult.data.depenses as DepensePartagee[]);
          setTotalDepenses((balancesResult.data.depenses as DepensePartagee[]).reduce((sum: number, d: DepensePartagee) => sum + d.montant_total, 0));
        }
        if (balancesResult.data.balances) {
          setBalances(balancesResult.data.balances as Balance[]);
        }
        if (balancesResult.data.totals) {
          // Convertir les totals en ParticipantSummary
          const summary: ParticipantSummary[] = balancesResult.data.totals.map((total: any) => {
            const participant = participantsResult.data?.find((p: Participant) => p.id === total.id);
            if (!participant) return null;
            return {
              participant,
              totalPaye: total.paye || 0,
              totalDu: total.du || 0,
              solde: total.solde || 0
            };
          }).filter((s: ParticipantSummary | null) => s !== null) as ParticipantSummary[];
          summary.sort((a, b) => b.solde - a.solde);
          setParticipantsSummary(summary);
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      alert('Impossible de charger les données de synthèse.');
    } finally {
      setLoading(false);
    }
  };


  const formatAmount = (amount: number) => {
    return amount.toLocaleString('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const getMonthLabel = () => {
    if (!mois || !annee) return '';
    const date = new Date(parseInt(annee), parseInt(mois) - 1, 1);
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  const navigateToMonth = (direction: 'prev' | 'next') => {
    if (!mois || !annee) return;
    const currentDate = new Date(parseInt(annee), parseInt(mois) - 1, 1);
    if (direction === 'prev') {
      currentDate.setMonth(currentDate.getMonth() - 1);
    } else {
      currentDate.setMonth(currentDate.getMonth() + 1);
    }
    const newAnnee = currentDate.getFullYear().toString();
    const newMois = (currentDate.getMonth() + 1).toString().padStart(2, '0');
    navigate(`/shared-expenses/summary/${newAnnee}/${newMois}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-dark-300">Chargement de la synthèse...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* En-tête */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/shared-expenses')}
          className="p-2 text-dark-300 hover:text-white hover:bg-dark-800 rounded-lg transition-colors duration-200"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-white">Synthèse des dépenses partagées</h1>
        </div>
      </div>

      {/* Sélecteur de mois */}
      <div className="flex items-center justify-center space-x-4 bg-dark-900 rounded-lg p-4">
        <button
          onClick={() => navigateToMonth('prev')}
          className="p-2 text-dark-300 hover:text-white hover:bg-dark-800 rounded-lg transition-colors duration-200"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <div className="flex-1 text-center">
          <h2 className="text-xl font-semibold text-white">{getMonthLabel()}</h2>
        </div>
        <button
          onClick={() => navigateToMonth('next')}
          className="p-2 text-dark-300 hover:text-white hover:bg-dark-800 rounded-lg transition-colors duration-200"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      {/* Statistiques générales */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-dark-900 rounded-lg p-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-dark-300 text-sm">Total dépenses</p>
              <p className="text-white text-2xl font-bold">{formatAmount(totalDepenses)}</p>
            </div>
          </div>
        </div>

        <div className="bg-dark-900 rounded-lg p-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-dark-300 text-sm">Participants</p>
              <p className="text-white text-2xl font-bold">{participants.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-dark-900 rounded-lg p-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
              <Euro className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-dark-300 text-sm">Part moyenne</p>
              <p className="text-white text-2xl font-bold">
                {participants.length > 0 ? formatAmount(totalDepenses / participants.length) : '0,00 €'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Soldes entre participants */}
      {balances.length > 0 && (
        <div className="bg-dark-900 rounded-lg overflow-hidden">
          <div className="p-6 border-b border-dark-800">
            <h2 className="text-xl font-semibold text-white">Soldes entre participants</h2>
          </div>
          
          <div className="divide-y divide-dark-800">
            {balances.map((balance, index) => (
              <div key={index} className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 flex-1">
                    <span className="text-red-500 font-bold">{balance.fromNom}</span>
                    <ArrowRight className="w-5 h-5 text-dark-400" />
                    <span className="text-green-500 font-bold">{balance.toNom}</span>
                  </div>
                  <span className="text-white font-bold text-lg">
                    {formatAmount(balance.montant)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Synthèse par participant */}
      <div className="bg-dark-900 rounded-lg overflow-hidden">
        <div className="p-6 border-b border-dark-800">
          <h2 className="text-xl font-semibold text-white">Récapitulatif par participant</h2>
        </div>
        
        <div className="divide-y divide-dark-800">
          {participantsSummary.map((summary) => (
            <div key={summary.participant.id} className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-white">{summary.participant.nom}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                    <div>
                      <p className="text-sm text-dark-300">Total payé</p>
                      <p className="text-white font-semibold">{formatAmount(summary.totalPaye)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-dark-300">Total dû</p>
                      <p className="text-white font-semibold">{formatAmount(summary.totalDu)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-dark-300">Solde</p>
                      <p className={`font-bold text-lg ${
                        summary.solde > 0 
                          ? 'text-green-500' 
                          : summary.solde < 0 
                            ? 'text-red-500' 
                            : 'text-white'
                      }`}>
                        {summary.solde > 0 ? '+' : ''}{formatAmount(summary.solde)}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="ml-6">
                  {summary.solde > 0 ? (
                    <div className="flex items-center space-x-2 text-green-500">
                      <TrendingUp className="w-5 h-5" />
                      <span className="text-sm font-medium">Créditeur</span>
                    </div>
                  ) : summary.solde < 0 ? (
                    <div className="flex items-center space-x-2 text-red-500">
                      <TrendingDown className="w-5 h-5" />
                      <span className="text-sm font-medium">Débiteur</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2 text-white">
                      <span className="text-sm font-medium">Équilibré</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Détail des dépenses */}
      {depenses.length > 0 && (
        <div className="bg-dark-900 rounded-lg overflow-hidden">
          <div className="p-6 border-b border-dark-800">
            <h2 className="text-xl font-semibold text-white">Détail des dépenses</h2>
          </div>
          
          <div className="divide-y divide-dark-800">
            {depenses.map((depense) => (
              <div key={depense.id} className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-white">{depense.description}</h3>
                    <p className="text-primary-500 font-medium">{formatDate(depense.date)}</p>
                    <p className="text-white text-xl font-bold mt-2">{formatAmount(depense.montant_total)}</p>
                  </div>
                </div>
                
                {depense.participations && depense.participations.length > 0 && (
                  <div className="border-t border-dark-700 pt-4">
                    <h4 className="text-dark-300 text-sm font-medium mb-3">Répartition :</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {depense.participations.map((participation) => (
                        <div key={participation.id} className="flex items-center justify-between p-3 bg-dark-800 rounded-lg">
                          <span className="text-white">
                            {participation.participant?.nom || 'Participant inconnu'}
                          </span>
                          <span className={`font-medium ${
                            participation.montant_paye === 0 
                              ? 'text-dark-400' 
                              : 'text-primary-500'
                          }`}>
                            {participation.montant_paye === 0 
                              ? "N'a pas payé" 
                              : formatAmount(participation.montant_paye)
                            }
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-center pt-6">
        <button
          onClick={() => navigate('/shared-expenses')}
          className="px-8 py-3 bg-dark-700 hover:bg-dark-600 text-white rounded-lg font-medium transition-colors duration-200"
        >
          Retour aux dépenses partagées
        </button>
      </div>
    </div>
  );
};

export default SharedExpensesSummary; 